import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import {
  CeiAutocompleteChipsComponent,
  CeiAutocompleteOptionComponent,
  CeiButtonComponent,
  CeiCheckboxComponent,
  CeiInputTextComponent,
  CeiPanelComponent,
  CeiPhotoUploadComponent,
  CEI_CPF_MASK,
} from '@cei-ufg/ngx-arquitetura-cei-frontend';
import { GruposApiService, ListRetrieveGrupo, Permissao, PermissoesApiService } from '@cei-ufg/ngx-cei-seguranca';
import { PessoaUsuarioFormState } from './pessoa-usuario-form.model';
import { ValidadorCpfDirective } from './validador-cpf';

/** Formulário compartilhado de pessoa/usuário no painel administrativo (criar e editar). */
@Component({
  selector: 'admin-pessoa-usuario-form',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    CeiInputTextComponent,
    CeiCheckboxComponent,
    CeiButtonComponent,
    CeiPanelComponent,
    CeiPhotoUploadComponent,
    CeiAutocompleteChipsComponent,
    CeiAutocompleteOptionComponent,
    ValidadorCpfDirective,
  ],
  templateUrl: './pessoa-usuario-form.component.html',
  styleUrls: ['./pessoa-usuario-form.component.scss'],
})
export class PessoaUsuarioFormComponent implements OnInit {
  @Input({ required: true }) form!: PessoaUsuarioFormState;
  @Input() mode: 'create' | 'edit' = 'create';
  /** Incrementado pelo pai após carregar/atualizar o registro; força remontagem dos autocompletes com chips do backend. */
  @Input() formVersao = 0;
  @Input() pessoaId?: number;
  @Output() readonly submitForm = new EventEmitter<NgForm>();
  @Output() readonly fotoSelected = new EventEmitter<File>();
  @Output() readonly fotoRemoved = new EventEmitter<void>();

  protected readonly cpfMask = CEI_CPF_MASK;
  /** Alinhado a MAX_FOTO_SIZE_BYTES em cei_apps_service/pessoas/managers.py */
  protected readonly maxFotoSizeBytes = 5 * 1024 * 1024;

  protected availablePermissions: Permissao[] = [];
  protected availableGroups: ListRetrieveGrupo[] = [];
  protected permissoesCatalogoCarregado = false;
  protected gruposCatalogoCarregado = false;

  /** True se qualquer campo de conta foi preenchido (regra tudo-ou-nada). */
  protected get temDadosConta(): boolean {
    return !!(
      this.form.login?.trim() ||
      this.form.email?.trim() ||
      this.form.password?.trim()
    );
  }

  /** Na criação, conta é opcional; se iniciada, login/e-mail/senha obrigatórios. */
  protected get exigeContaUsuario(): boolean {
    return this.mode === 'create' && this.temDadosConta;
  }

  /** Rótulo de opção e filtro de busca de permissão (descrição + slug). */
  protected readonly formatarPermissao = (valor: unknown): string => {
    const permissao = valor as Permissao;
    return `${permissao.name} (${permissao.codename})`;
  };

  /** Rótulo do chip de permissão (somente slug/codename). */
  protected readonly formatarPermissaoChip = (valor: unknown): string =>
    (valor as Permissao).codename;

  /** Rótulo de opção e filtro de busca de grupo. */
  protected readonly formatarGrupo = (valor: unknown): string => (valor as ListRetrieveGrupo).name;

  /** Chave estável para deduplicar permissões selecionadas. */
  protected readonly extrairChavePermissao = (valor: unknown): string =>
    String((valor as Permissao).id);

  /** Chave estável para deduplicar grupos selecionados. */
  protected readonly extrairChaveGrupo = (valor: unknown): number => (valor as ListRetrieveGrupo).id;

  private readonly permissoesApi = inject(PermissoesApiService);
  private readonly gruposApi = inject(GruposApiService);
  private readonly cdr = inject(ChangeDetectorRef);

  ngOnInit(): void {
    this.carregarPermissoesDisponiveis();
    this.carregarGruposDisponiveis();
  }

  onSubmit(form: NgForm): void {
    this.submitForm.emit(form);
  }

  onFotoSelected(file: File): void {
    this.fotoSelected.emit(file);
  }

  onFotoRemoved(): void {
    this.form.fotoUrl = null;
    this.fotoRemoved.emit();
  }

  /** Chave de remontagem do autocomplete de permissões (catálogo + seleção vinda do backend). */
  protected chaveAutocompletePermissoes(): string {
    const ids = this.form.user_permissions.map((permissao) => permissao.id).join('|');
    return `${this.formVersao}|${this.permissoesCatalogoCarregado}|${ids}`;
  }

  /** Chave de remontagem do autocomplete de grupos (catálogo + seleção vinda do backend). */
  protected chaveAutocompleteGrupos(): string {
    const ids = this.form.groups.map((grupo) => grupo.id).join('|');
    return `${this.formVersao}|${this.gruposCatalogoCarregado}|${ids}`;
  }

  /** Carrega catálogo de permissões para o autocomplete de chips. */
  private carregarPermissoesDisponiveis(): void {
    this.permissoesApi.permissaoList({}).subscribe({
      next: (data) => {
        this.availablePermissions = data.results ?? [];
        this.permissoesCatalogoCarregado = true;
        this.cdr.markForCheck();
      },
      error: () => {
        this.availablePermissions = [];
        this.permissoesCatalogoCarregado = true;
        this.cdr.markForCheck();
      },
    });
  }

  /** Carrega catálogo de grupos para o autocomplete de chips. */
  private carregarGruposDisponiveis(): void {
    this.gruposApi.grupoList({}).subscribe({
      next: (data) => {
        this.availableGroups = data.results ?? [];
        this.gruposCatalogoCarregado = true;
        this.cdr.markForCheck();
      },
      error: () => {
        this.availableGroups = [];
        this.gruposCatalogoCarregado = true;
        this.cdr.markForCheck();
      },
    });
  }
}
